export function getGroupManagerId(group) {
  const m = group?.manager;
  return typeof m === "object" ? m?.id : m;
}

export function normalizeMemberIds(members) {
  if (!Array.isArray(members)) return [];
  return members
    .map((m) => (typeof m === "object" ? m?.id : m))
    .filter((id) => id != null)
    .map((id) => Number(id))
    .filter((id) => !Number.isNaN(id));
}

export function getResponseEmployeeId(respRow) {
  const direct =
    respRow?.employee_id ?? respRow?.employee ?? respRow?.employeeId ?? null;
  const fromObj = typeof direct === "object" ? direct?.id : direct;
  const n = Number(fromObj);
  return Number.isNaN(n) ? null : n;
}

export function matchesDebriefSchedule(reportGroup, scheduleId) {
  const targetNum = scheduleId != null ? Number(scheduleId) : null;
  const targetStr = scheduleId != null ? String(scheduleId) : null;
  if (!targetStr) return false;

  const firstRow = Array.isArray(reportGroup?.responses)
    ? reportGroup.responses[0]
    : null;

  const candidates = [
    reportGroup?.debrief_schedule,
    reportGroup?.debrief_schedule_id,
    reportGroup?.schedule,
    reportGroup?.schedule_id,
    reportGroup?.survey_schedule,
    reportGroup?.survey_schedule_id,
    reportGroup?.survey_event,
    reportGroup?.survey_event_id,

    // Often the schedule ids are on the response rows rather than the group container
    firstRow?.debrief_schedule,
    firstRow?.debrief_schedule_id,
    firstRow?.schedule,
    firstRow?.schedule_id,
    firstRow?.survey_schedule,
    firstRow?.survey_schedule_id,
    firstRow?.survey_event,
    firstRow?.survey_event_id,

    // Sometimes nested under response
    firstRow?.response?.debrief_schedule_id,
    firstRow?.response?.schedule_id,
    firstRow?.response?.survey_schedule_id,
    firstRow?.response?.survey_event_id,
  ].filter((v) => v != null);

  return candidates.some((v) => {
    if (targetNum != null && !Number.isNaN(targetNum) && Number(v) === targetNum)
      return true;
    return String(v) === targetStr;
  });
}

export function flattenReportResponsesForSchedule(cultureAuditGroups, scheduleId) {
  const groups = Array.isArray(cultureAuditGroups) ? cultureAuditGroups : [];
  const matched = groups.filter((g) => matchesDebriefSchedule(g, scheduleId));
  return matched
    .flatMap((g) => (Array.isArray(g?.responses) ? g.responses : []))
    .filter(Boolean);
}

export function buildParticipationThresholdMatches({
  permissionGroups,
  employees,
  scheduleResponses,
  threshold,
}) {
  const emps = Array.isArray(employees) ? employees : [];
  const t = Number(threshold);
  const min = Number.isNaN(t) ? 5 : t;

  const countsByEmployeeId = new Map();
  (Array.isArray(scheduleResponses) ? scheduleResponses : []).forEach((r) => {
    const eid = getResponseEmployeeId(r);
    if (!eid) return;
    countsByEmployeeId.set(eid, (countsByEmployeeId.get(eid) || 0) + 1);
  });

  const matches = [];
  (Array.isArray(permissionGroups) ? permissionGroups : []).forEach((g) => {
    const managerId = getGroupManagerId(g);
    const manager = managerId
      ? emps.find((e) => Number(e.id) === Number(managerId))
      : null;

    const memberIds = normalizeMemberIds(g?.members);
    if (memberIds.length === 0) return;

    let responseCount = 0;
    for (const memberId of memberIds) {
      responseCount += countsByEmployeeId.get(memberId) || 0;
    }

    if (responseCount < min) {
      matches.push({
        groupId: g?.id,
        managerId,
        managerName: manager
          ? `${manager.first_name || ""} ${manager.last_name || ""}`.trim() ||
            manager.email
          : managerId
          ? `Manager #${managerId}`
          : "Unknown manager",
        managerEmail: manager?.email || "",
        memberCount: memberIds.length,
        responseCount,
      });
    }
  });

  matches.sort((a, b) => (a.responseCount || 0) - (b.responseCount || 0));
  return matches;
}

// Preferred for this scanner: use debrief-schedule "compliance" list.
// compliance ids represent employees who completed the survey.
export function buildComplianceThresholdMatches({
  permissionGroups,
  employees,
  complianceIds,
  threshold,
}) {
  const emps = Array.isArray(employees) ? employees : [];
  const t = Number(threshold);
  const min = Number.isNaN(t) ? 5 : t;

  const complianceSet = new Set(
    (Array.isArray(complianceIds) ? complianceIds : [])
      .map((id) => Number(id))
      .filter((id) => !Number.isNaN(id))
  );

  const matches = [];
  (Array.isArray(permissionGroups) ? permissionGroups : []).forEach((g) => {
    const managerId = getGroupManagerId(g);
    const manager = managerId
      ? emps.find((e) => Number(e.id) === Number(managerId))
      : null;

    const memberIds = normalizeMemberIds(g?.members);
    if (memberIds.length === 0) return;

    let completedCount = 0;
    for (const memberId of memberIds) {
      if (complianceSet.has(memberId)) completedCount += 1;
    }

    if (completedCount < min) {
      matches.push({
        groupId: g?.id,
        managerId,
        managerName: manager
          ? `${manager.first_name || ""} ${manager.last_name || ""}`.trim() ||
            manager.email
          : managerId
          ? `Manager #${managerId}`
          : "Unknown manager",
        managerEmail: manager?.email || "",
        memberCount: memberIds.length,
        completedCount,
      });
    }
  });

  matches.sort((a, b) => (a.completedCount || 0) - (b.completedCount || 0));
  return matches;
}


